/**
 * useMyRodeos
 *
 * Query hook for the authenticated caller's rodeos, backed by
 * `GET /api/jackpot-ops/my/rodeos`. The backend scopes results to the
 * caller's organization memberships (expanding ALL-org automatically),
 * so the frontend does not need to pass organization_id explicitly.
 *
 * Key design notes:
 *   - Uses rodeoOpsKeys.all as the cache prefix so mutations that
 *     invalidate `rodeoOpsKeys.all` (e.g. useDeleteRodeo) automatically
 *     bust this cache too.
 *   - Filters object is omitted from the query key entirely when absent
 *     to avoid the "undefined-in-key" bug documented in MEMORY.md
 *     (partialDeepEqual fails on undefined vs object, breaking
 *     invalidateQueries prefix matching).
 *   - Matches the response envelope produced by listMyRodeos on the
 *     backend: `{ data: Rodeo[]; total: number; page: number;
 *     page_size: number; has_more: boolean }`.
 */
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../api/http-client';
import { rodeoOpsKeys } from '../api/extensions/useRodeoOps';

export interface MyRodeosFilters {
  is_published?: boolean;
  page?: number;
  page_size?: number;
  /** When false, the query is disabled (useful to wait for auth context). */
  enabled?: boolean;
}

interface MyRodeo {
  id: string;
  name: string;
  rodeo_or_jackpot: string;
  is_published: boolean;
  events?: any[];
  /**
   * Authoritative "may this rodeo be hard-deleted" flag, computed server-side
   * (no signups AND draw not initialized AND not published). Drives whether
   * the trash icon renders on the My Rodeos page.
   */
  can_delete?: boolean;
  /** The 3 sub-flags behind `can_delete`, for tooltip messaging. */
  deletability?: {
    is_published: boolean;
    draw_initialized: boolean;
    has_signups: boolean;
    can_delete: boolean;
  };
  [key: string]: unknown;
}

export interface MyRodeosResponse {
  data: MyRodeo[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

/**
 * Build a sanitized filters object for the query key.
 * Only includes fields that are actually set — avoids polluting the
 * cache identity with `undefined` values.
 */
function sanitizeFilters(
  filters?: MyRodeosFilters
): Omit<MyRodeosFilters, 'enabled'> | undefined {
  if (!filters) return undefined;
  const { enabled: _enabled, ...rest } = filters;
  const sanitized: Record<string, unknown> = {};
  if (rest.is_published !== undefined)
    sanitized.is_published = rest.is_published;
  if (rest.page !== undefined) sanitized.page = rest.page;
  if (rest.page_size !== undefined) sanitized.page_size = rest.page_size;
  return Object.keys(sanitized).length > 0
    ? (sanitized as Omit<MyRodeosFilters, 'enabled'>)
    : undefined;
}

export function useMyRodeos(filters?: MyRodeosFilters) {
  const sanitized = sanitizeFilters(filters);

  const qs = new URLSearchParams();
  if (sanitized?.is_published !== undefined)
    qs.set('is_published', String(sanitized.is_published));
  if (sanitized?.page) qs.set('page', String(sanitized.page));
  if (sanitized?.page_size) qs.set('page_size', String(sanitized.page_size));
  const query = qs.toString() ? `?${qs}` : '';

  return useQuery({
    queryKey: sanitized
      ? ([...rodeoOpsKeys.all, 'my', sanitized] as const)
      : ([...rodeoOpsKeys.all, 'my'] as const),
    queryFn: () =>
      httpClient.get<MyRodeosResponse>(`/jackpot-ops/my/rodeos${query}`),
    enabled: filters?.enabled !== false,
  });
}
